import { addonKindsOf } from './addons.js';
import { z } from 'zod';

// Java Edition 1.21.x server.properties: https://minecraft.wiki/w/Server.properties
// Removed in 1.21.2: spawn-animals/spawn-npcs; in 1.21.9: allow-nether/spawn-monsters/enable-command-block/pvp.
// game-setting-rules.ts routes current versions to game rules; these definitions remain the legacy property defaults.
// Defaults/ranges cross-checked with https://docs.papermc.io/paper/reference/server-properties/
// max-tick-time is a Java long; the panel accepts only JS safe integers to avoid rounding.
const integer = (min: number, max = 2147483647) => z.number().int().min(min).max(max);
export const advancedGameShape = {
  'allow-nether': z.boolean(),
  'view-distance': integer(3, 32),
  'simulation-distance': integer(3, 32),
  'spawn-monsters': z.boolean(),
  'spawn-animals': z.boolean(),
  'spawn-npcs': z.boolean(),
  'white-list': z.boolean(),
  'enforce-whitelist': z.boolean(),
  'player-idle-timeout': integer(0),
  'force-gamemode': z.boolean(),
  'op-permission-level': integer(0, 4),
  'enable-command-block': z.boolean(),
  'broadcast-console-to-ops': z.boolean(),
  'hide-online-players': z.boolean(),
  'enforce-secure-profile': z.boolean(),
  'max-tick-time': integer(-1, Number.MAX_SAFE_INTEGER),
  'network-compression-threshold': integer(-1),
  'entity-broadcast-range-percentage': integer(10, 1000),
  'sync-chunk-writes': z.boolean(),
};
export const advancedGameDefaults = {
  'allow-nether': true,
  'view-distance': 10,
  'simulation-distance': 10,
  'spawn-monsters': true,
  'spawn-animals': true,
  'spawn-npcs': true,
  'white-list': false,
  'enforce-whitelist': false,
  'player-idle-timeout': 0,
  'force-gamemode': false,
  'op-permission-level': 4,
  'enable-command-block': false,
  'broadcast-console-to-ops': true,
  'hide-online-players': false,
  'enforce-secure-profile': true,
  'max-tick-time': 60000,
  'network-compression-threshold': 256,
  'entity-broadcast-range-percentage': 100,
  'sync-chunk-writes': true,
};

// Paper ver/26.1.2 CraftServer#getAllowEnd reads settings.allow-end.
// https://github.com/PaperMC/Paper/blob/ver/26.1.2/paper-server/src/main/java/org/bukkit/craftbukkit/CraftServer.java
// Purpur ver/26.1.2 inherits Paper's CraftServer (upstream paper in build.gradle.kts).
// https://github.com/PurpurMC/Purpur/blob/ver/26.1.2/build.gradle.kts
export const supportsEndSetting = (family: string) => addonKindsOf(family).plugins;

export function propertyReadOnlyReason(key: string): string {
  if (['generate-structures', 'level-seed', 'level-type', 'generator-settings'].includes(key))
    return '生成済みの地形は変わりません。「ワールドの生成」で次回の生成設定を変更してください。';
  if (/^(server-port|server-ip|online-mode|enable-rcon|enable-query|level-name|rcon\..*|query\..*)$/.test(key))
    return '接続・認証・配置に使うためパネルが管理しています。';
  return '安全な編集項目の許可リストに含まれていないため、表示のみです。';
}

export const propertyGroups = {
  ワールド: ['view-distance', 'simulation-distance', 'spawn-monsters', 'spawn-animals', 'spawn-npcs', 'allow-nether'],
  プレイヤー: ['white-list', 'enforce-whitelist', 'player-idle-timeout', 'force-gamemode', 'op-permission-level'],
  'チャット・表示': [
    'enable-command-block',
    'broadcast-console-to-ops',
    'hide-online-players',
    'enforce-secure-profile',
  ],
  性能: ['max-tick-time', 'network-compression-threshold', 'entity-broadcast-range-percentage', 'sync-chunk-writes'],
} as const;
export const propertyDescriptions: Record<keyof typeof advancedGameShape, string> = {
  'allow-nether': 'ネザーを使う。無効にしてもワールドデータは残ります。',
  'view-distance': 'プレイヤーに送る地形の距離（チャンク）。大きいほど負荷が増えます。',
  'simulation-distance': '生物やブロックを動かす範囲（チャンク）。大きいほど負荷が増えます。',
  'spawn-monsters': 'モンスターの自然発生を許可します。',
  'spawn-animals': '動物の自然発生を許可します。',
  'spawn-npcs': '村人の出現を許可します。',
  'white-list': '許可リストに登録されたプレイヤーだけ参加できます。',
  'enforce-whitelist': '許可リスト外の参加者を切断します。',
  'player-idle-timeout': '操作しないプレイヤーを切断するまでの分数。0 は無効です。',
  'force-gamemode': '再参加時に既定のゲームモードに戻します。',
  'op-permission-level': '新しく OP にするプレイヤーの権限（0〜4）。',
  'enable-command-block': 'コマンドブロックを有効にします。',
  'broadcast-console-to-ops': 'コンソールのコマンド結果を OP に通知します。',
  'hide-online-players': 'サーバー一覧で参加者の名前を隠します。',
  'enforce-secure-profile': 'Mojang の署名があるプロフィールを必須にします。',
  'max-tick-time': '処理停止と判断する時間（ミリ秒）。-1 は監視を無効にします。',
  'network-compression-threshold': '通信を圧縮する最小バイト数。-1 は無効、0 は常に圧縮します。',
  'entity-broadcast-range-percentage': '生物などの表示範囲を既定の何％にするか（10〜1000）。',
  'sync-chunk-writes': '地形の書き込みを同期します。無効にすると障害時の保存に影響することがあります。',
};
